import { LinkOutlined } from '@ant-design/icons'
import { Button, Card, Select, Table, TableColumnsType, TablePaginationConfig } from 'antd'
import { ReferralCommissionType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import { LEVEL_LABEL, UserRewardLevelEnum } from 'constants/affiliate'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { TOKEN_LABEL, TokenTypeEnum } from 'constants/token'
import { PAGES } from 'routes'
import { logInfo } from 'utils/logger'
import { getLabelToValue } from 'utils/object'

export default function AffiliateCommission() {
  const navigate = useNavigate()
  const [dataAffiliates, setDataAffiliates] = useState([])
  const [total, setTotal] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [current, setCurrent] = useState(DEFAULT_PAGE_INDEX)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [type, setType] = useState<TokenTypeEnum | null>(null)
  const [level, setLevel] = useState<UserRewardLevelEnum | null>(null)
  const [address, setAddress] = useState<string>('')
  const [fromAddress, setFromAddress] = useState<string>('')
  const [txHash, setTxHash] = useState<string>('')

  const getDataAffiliates = useCallback(
    async (page = DEFAULT_PAGE_INDEX, limit = DEFAULT_PAGE_SIZE) => {
      setLoading(true)
      try {
        const res = await AppAdmin.getListReferral({
          page,
          limit,
          type: type,
          ownerAddress: address,
          fromAddress,
          fromHash: txHash,
          level: level,
        })
        if (res?.data?.success) {
          const { results, totalResults } = res.data.data || {}
          setDataAffiliates(results)
          setTotal(totalResults)
        }
      } catch (error) {
        setDataAffiliates([])
        setTotal(0)
        logInfo('Error: ', error)
      } finally {
        setLoading(false)
      }
    },
    [address, fromAddress, level, txHash, type]
  )

  useEffect(() => {
    getDataAffiliates(current, pageSize)
  }, [current, pageSize, txHash, fromAddress, address, type, level, getDataAffiliates])

  useEffect(() => {
    setCurrent(DEFAULT_PAGE_INDEX)
    setPageSize(DEFAULT_PAGE_SIZE)
  }, [type, txHash, fromAddress, address, level])

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || DEFAULT_PAGE_INDEX)
    setPageSize(newPageSize || DEFAULT_PAGE_SIZE)
  }

  const renderAmount = (type: TokenTypeEnum, defaultType: TokenTypeEnum, value: any) => {
    if (type === defaultType) {
      return new Intl.NumberFormat('en-US').format(value)
    }
    return '-'
  }

  const columns: TableColumnsType<ReferralCommissionType> = [
    {
      title: 'User Address',
      dataIndex: 'ownerAddress',
      key: 'ownerAddress',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress
            address={text}
            render={(text) => {
              return (
                <span
                  style={{ cursor: 'pointer', color: 'blue' }}
                  onClick={() => {
                    navigate(`${PAGES.AFFILIATES_TREE}/${record?.ownerAddress}`)
                  }}
                >
                  {text}
                </span>
              )
            }}
          />
        </div>
      ),
    },
    {
      title: 'From Address',
      dataIndex: 'fromAddress',
      key: 'fromAddress',
      render: (text, record) => (
        <>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <LinkAddress
              address={text}
              render={(text) => {
                return <span>{text}</span>
              }}
            />
          </div>
          <div>{getLabelToValue(LEVEL_LABEL, record.level)}</div>
        </>
      ),
    },
    {
      title: 'Stake Amount',
      dataIndex: 'stakeAmount',
      key: 'stakeAmount',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Commission Rate',
      dataIndex: 'percent',
      key: 'percent',
      render: (text) => <span>{text}%</span>,
    },
    {
      title: 'Commission Amount (USDC)',
      dataIndex: 'reward',
      key: 'reward',
      render: (text, record) => {
        return <span>{renderAmount(record.type, TokenTypeEnum.USDC, text)}</span>
      },
    },
    {
      title: 'Commission Amount (MEY)',
      dataIndex: 'reward',
      key: 'reward',
      render: (text, record) => <span>{renderAmount(record.type, TokenTypeEnum.MEY, text)}</span>,
    },
    {
      title: 'Commission Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'From Hash',
      dataIndex: 'fromHash',
      key: 'fromHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={text} icon={<LinkOutlined />} type="tx" />
        </div>
      ),
    },
  ]

  return (
    <Card style={{ margin: '0 auto' }}>
      <div className="user-search">
        <input
          type="text"
          placeholder="User Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
        <input
          type="text"
          placeholder="From Address"
          value={fromAddress}
          onChange={(e) => setFromAddress(e.target.value)}
        />
        <input
          type="phone"
          placeholder="TxHash"
          value={txHash}
          onChange={(e) => setTxHash(e.target.value)}
        />
        <Select
          allowClear
          placeholder="By type"
          options={TOKEN_LABEL}
          value={type}
          onChange={(value) => {
            setCurrent(DEFAULT_PAGE_INDEX)
            setType(value)
          }}
          style={{ width: '200px', textAlign: 'start', minHeight: '38px' }}
        />
        <Select
          allowClear
          placeholder="By Level"
          options={LEVEL_LABEL}
          value={level}
          onChange={(value) => {
            setCurrent(DEFAULT_PAGE_INDEX)
            setLevel(value)
          }}
          style={{ width: '200px', textAlign: 'start', minHeight: '38px' }}
        />
        <Button
          type="primary"
          onClick={() => {
            setCurrent(1)
            setPageSize(10)
            getDataAffiliates(1, 10)
          }}
        >
          Search
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={dataAffiliates}
        loading={loading}
        pagination={total < 11 ? false : { total, current, pageSize }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        bordered
      />
    </Card>
  )
}
